import { Amplify, ResourcesConfig } from 'aws-amplify';
import { fetchAuthSession, getCurrentUser, signInWithRedirect, AuthSession } from 'aws-amplify/auth';
import { sessionStorage } from 'aws-amplify/utils';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAmplifyAuthConfig, getAppSyncConfig, getRestApiConfig } from 'src/components/context/AuthContextUtils';
import { configureTheLogger } from 'src/logger';
import { ADMIN_LDAP_GROUPS, HUDDLEPLUS_ADMIN_LDAP_GROUPS, VPC_CIDR_EDIT_LDAP_GROUPS, eLDAPGroup } from '../../constants/AppConstants';
import { LoadingSpinner } from '../generic-components/LoadingSpinner';
import { LoadingStatus, UserAuthContext } from './AppContextModels';
import { RumClient } from 'src/services/aws/CloudWatchRumService';

const initialData: UserAuthContext = {
  userAuthDataLoadingStatus: LoadingStatus.Loading
} as UserAuthContext;
const AuthContextDetails = createContext(initialData);
export const useAuth = () => useContext(AuthContextDetails);

// Provider component that wraps your app and makes auth object
export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userAuthData = useAuthProvider();

  if (userAuthData?.userAuthDataLoadingStatus === LoadingStatus.Loading) {
    return <LoadingSpinner />;
  }

  configureTheLogger(userAuthData);
  RumClient.initialize(userAuthData.Alias);

  return <AuthContextDetails.Provider value={userAuthData}>{children}</AuthContextDetails.Provider>;
};

const useAuthProvider = () => {
  const [userCognitoAuthData, setUserCognitoAuthData] = useState<UserAuthContext>({
    ...initialData,
    auth_status_message: 'Loading',
    userAuthDataLoadingStatus: LoadingStatus.Loading
  });

  useEffect(() => {
    authenticateAndSetUserDetails();
  }, []);

  const authenticateAndSetUserDetails = () => {
    Amplify.configure(getAmplifyAuthConfig());

    const configureAppSync = async (maxRetries = 3) => {
      let lastError: any;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const authConfig = getAmplifyAuthConfig();
          const awsAppSync = await getAppSyncConfig();
          const restApiConfig = getRestApiConfig();

          const combinedConfig = {
            ...authConfig,
            API: {
              ...awsAppSync.API,
              ...restApiConfig.API
            }
          };

          Amplify.configure(combinedConfig as ResourcesConfig);
          return;
        } catch (error: any) {
          lastError = error;
          console.error(`Unable to fetch Config (attempt ${attempt}/${maxRetries})`, error);
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          }
        }
      }
      const error = new Error('Unable to configure AppSync after maximum retries');
      (error as any).cause = lastError;
      throw error;
    };

    const REDIRECT_KEY = 'auth_redirect_url';

    const signInWithAmazonFederate = async () => {
      try {
        // Preserve the original URL so we can restore it after auth redirect
        await sessionStorage.setItem(REDIRECT_KEY, window.location.href);
        await signInWithRedirect({
          provider: { custom: 'AmazonFederate' }
        });
        const session = await fetchAuthSession();
        const sessionDetails = await getSessionDetails(session);
        setUserCognitoAuthData(sessionDetails);
      } catch (error: any) {
        // logger isn't configured at this point.
        console.error('Unable to sign in with AmazonFederate', error);
        setUserCognitoAuthData({
          ...userCognitoAuthData,
          auth_status_message: 'Unable to sign in with AmazonFederate',
          userAuthDataLoadingStatus: LoadingStatus.Loading
        });
      }
    };

    getCurrentUser()
      .then(async () => {
        // Ensure auth session is fully established before configuring AppSync.
        // On first load, Cognito tokens may not be hydrated yet when getCurrentUser resolves,
        // causing downstream AWS SDK calls (Secrets Manager, SSM) to fail with ERR_SOCKET_NOT_CONNECTED.
        const session = await fetchAuthSession({ forceRefresh: false });
        try {
          await configureAppSync();
        } catch (error: any) {
          console.error('AppSync configuration failed after all retries', error);
          const sessionDetails = await getSessionDetails(session);
          setUserCognitoAuthData({
            ...sessionDetails,
            auth_status_message: 'AppSync configuration failed — some features may be unavailable',
            userAuthDataLoadingStatus: LoadingStatus.Failed
          });
          return;
        }
        const sessionDetails = await getSessionDetails(session);
        setUserCognitoAuthData(sessionDetails);

        // Restore the original URL after successful authentication
        const redirectUrl = await sessionStorage.getItem(REDIRECT_KEY);
        await sessionStorage.removeItem(REDIRECT_KEY);
        if (redirectUrl && redirectUrl !== window.location.href) {
          window.location.replace(redirectUrl);
          return;
        }
      })
      .catch(() => {
        signInWithAmazonFederate();
      });
  };

  /**
   * Retrieves and formats the LDAP groups from the credentials payload.
   * @param session The AWS auth session object containing the ID token payload.
   * @returns A properly formatted string array of LDAP groups.
   */
  const formatUserLDAPs = (session: AuthSession): string[] => {
    // if user is not part of any ldap, we receive it as "[]"
    const ldapGroups = session.tokens?.idToken?.payload?.['custom:LDAP_GROUPS'];
    // TEMP DIAGNOSTIC (annunna, 2026-06-11): Log the LDAP groups Cognito returns in the
    // ID token. Cognito occasionally serves a stale/delayed copy of custom:LDAP_GROUPS,
    // so we want to observe raw vs. parsed values in production for a few days. This is
    // non-sensitive (LDAP group names only — no tokens/PII). The app logger (KatalLogger)
    // is intentionally not used here: it is only configured post-auth, so it is undefined
    // at this point. Remove after confirming Cognito propagation behavior.
    console.log('[Auth] Raw LDAP groups from token:', JSON.stringify(ldapGroups));
    // Check if ldapGroups is a string and try to parse it as JSON
    let formatted: string[] = [];
    if (typeof ldapGroups === 'string') {
      try {
        const parsedLdapGroups = JSON.parse(ldapGroups);
        formatted =
          Array.isArray(parsedLdapGroups) && parsedLdapGroups.every((item) => typeof item === 'string') ? (parsedLdapGroups as string[]) : [];
      } catch (error: any) {
        console.error('Failed to parse ldapGroups string: ', error);
        formatted = [];
      }
    } else {
      // If ldapGroups is already an array, use it; otherwise, return an empty array
      formatted = Array.isArray(ldapGroups) && ldapGroups.every((item) => typeof item === 'string') ? (ldapGroups as string[]) : [];
    }

    // Normalize to lowercase so all downstream LDAP group comparisons (isDev, isAdmin,
    // isOEReadOnly, isVpcCidrEditor, isHuddlePlusAdmin, Kingpin access) are case-insensitive
    // against the lowercase constants in AppConstants, regardless of the casing Cognito delivers.
    formatted = formatted.map((group) => group.toLowerCase());

    // TEMP DIAGNOSTIC (annunna, 2026-06-11): see note above — remove with the raw log.
    console.log('[Auth] Formatted LDAP groups:', JSON.stringify(formatted));
    return formatted;
  };

  const getSessionDetails = async (session: AuthSession) => {
    const userLDAPs: string[] = formatUserLDAPs(session);
    const idTokenPayload = session.tokens?.idToken?.payload;

    const identities = idTokenPayload?.['identities'];
    const userId = Array.isArray(identities) && identities.length > 0 ? (identities[0] as any)?.userId : undefined;

    const sessionDetails = {
      Alias: userId,
      CognitoSub: (idTokenPayload?.sub as string) ?? '',
      DisplayName: idTokenPayload?.['custom:DISPLAY_NAME'] as string,
      GivenName: idTokenPayload?.['custom:GIVEN_NAME'] as string,
      Email: idTokenPayload?.['custom:EMAIL'] as string,
      userLDAPGroups: userLDAPs,
      isDev: userLDAPs.includes(eLDAPGroup.DEV_LDAP),
      isAdmin: ADMIN_LDAP_GROUPS.some((group) => userLDAPs.includes(group)),
      isOEReadOnly: userLDAPs.includes(eLDAPGroup.OE_RO_LDAP),
      isVpcCidrEditor: VPC_CIDR_EDIT_LDAP_GROUPS.some((group) => userLDAPs.includes(group)),
      isHuddlePlusAdmin: HUDDLEPLUS_ADMIN_LDAP_GROUPS.some((group) => userLDAPs.includes(group)),
      auth_status_message: 'SSO completed',
      userAuthDataLoadingStatus: LoadingStatus.Completed
    } as UserAuthContext;

    return sessionDetails;
  };

  return userCognitoAuthData;
};
